/**
 * Плашка «У вас включён VPN» сверху, экран под ней заблокирован.
 *
 * Решение владельца 18.09.2026: с включённым VPN приложением пользоваться нельзя вовсе — ни войти,
 * ни оформить заказ, ни оплатить. Сказать это надо сразу при входе, а не отказом банка в конце
 * оплаты. Не отдельная страница и не кнопка «проверить»: плашка сверху, под ней затемнённое и
 * неживое приложение, и плашка уходит сама, как только VPN выключили.
 *
 * Приложение под плашкой не размонтируется, а только замораживается (`inert`): черновик заказа и
 * открытый экран переживают включение VPN посреди работы. Пока идёт первая проверка (секунду-две),
 * приложение уже видно, но нажать в нём ещё ничего нельзя.
 */
import type { ReactNode } from 'react';
import { useVpnGate } from './useVpnGate';
import type { VpnVerdict } from './vpnCheck';

export function VpnGate({
  children,
  check,
}: {
  children: ReactNode;
  /** Для тестов: подменная проверка вместо похода к серверу. */
  check?: () => Promise<VpnVerdict | null>;
}) {
  const gate = useVpnGate(check);
  const frozen = gate.status !== 'clear';

  return (
    <>
      <div className="h-full" inert={frozen} aria-hidden={frozen || undefined}>
        {children}
      </div>
      {gate.status === 'blocked' ? <VpnBanner message={gate.message} /> : null}
    </>
  );
}

function VpnBanner({ message }: { message: string }) {
  return (
    <div className="fixed inset-0 z-[1000]" data-testid="vpn-gate-blocked">
      {/* Затемнение: приложение видно, но нажать в нём ничего нельзя. */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div
        role="alert"
        aria-labelledby="vpn-gate-title"
        aria-describedby="vpn-gate-text"
        className="absolute inset-x-0 top-0 bg-accent px-5 pb-4 text-accent-ink shadow-lg"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto flex max-w-[560px] flex-col gap-1">
          <p id="vpn-gate-title" className="text-[16.5px] font-bold leading-snug">
            У вас включён VPN
          </p>
          <p id="vpn-gate-text" className="text-[14.5px] leading-snug">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}
